import React, { useMemo } from 'react';
import DataGrid from '../../../../common/DataGrid/DataGrid.jsx';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import { useOptionsDataGridFilter } from '../../../../common/DataGrid/filters/DataGridFilters.jsx';
import { useUniqueOptionsDataGridFilter } from '../../../../common/DataGrid/filters/useUniqueOptionsDataGridFilter.jsx';
import { dataGridTextSorter } from '../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import PropTypes from 'prop-types';
import { Spinner } from '../../../../common/index.jsx';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../../../../common/controls/Controls.jsx';
import { UserOutlined, EditOutlined } from '@ant-design/icons';

const UsersTable = ({
  users,
  onDetailClick,
  onEditUserProfileClick,
  showRolesEditIcon,
}) => {
  const { t } = useTranslation();
  const firstNameTextFilterProps = useDataGridTextFilter('first_name');
  const lastNameTextFilterProps = useDataGridTextFilter('last_name');
  const mailTextFilterProps = useDataGridTextFilter('mail');
  const departmentCodeFilterProps = useUniqueOptionsDataGridFilter(
    'departmentCode',
    users,
    {
      width: 150,
    },
  );
  const studyGroupTextFilterProps = useDataGridTextFilter('studyGroup');
  const userTypeFilterProps = useOptionsDataGridFilter('userType', {
    teacher: t('Učiteľ'),
    student: t('Študent'),
  });

  const columns = useMemo(
    () => [
      {
        title: t('Priezvisko'),
        dataIndex: 'last_name',
        ellipsis: true,
        sorter: dataGridTextSorter('last_name'),
        defaultSortOrder: 'ascend',
        ...lastNameTextFilterProps,
      },
      {
        title: t('Meno'),
        dataIndex: 'first_name',
        ellipsis: true,
        sorter: dataGridTextSorter('first_name'),
        ...firstNameTextFilterProps,
      },
      {
        title: t('E-mail'),
        dataIndex: 'mail',
        ellipsis: true,
        sorter: dataGridTextSorter('mail'),
        ...mailTextFilterProps,
      },
      {
        title: t('Typ'),
        dataIndex: 'userType',
        ellipsis: true,
        render: (value) => {
          if (value === 'teacher') {
            return t('Učiteľ');
          }

          if (value === 'student') {
            return t('Študent');
          }

          return '-';
        },
        sorter: dataGridTextSorter('userType', (value) => {
          if (value === 'teacher') {
            return t('Učiteľ');
          }

          if (value === 'student') {
            return t('Študent');
          }

          return '';
        }),
        ...userTypeFilterProps,
      },
      {
        title: t('Katedra'),
        dataIndex: 'departmentCode',
        ellipsis: true,
        render: (value) => value || '-',
        sorter: dataGridTextSorter('departmentCode'),
        ...departmentCodeFilterProps,
      },
      {
        title: t('Št. skupina'),
        dataIndex: 'studyGroup',
        ellipsis: true,
        render: (value) => value || '-',
        sorter: dataGridTextSorter('studyGroup'),
        ...studyGroupTextFilterProps,
      },
    ],
    [
      firstNameTextFilterProps,
      lastNameTextFilterProps,
      mailTextFilterProps,
      userTypeFilterProps,
      departmentCodeFilterProps,
      studyGroupTextFilterProps,
      t,
    ],
  );

  const customSettings = (user) => {
    return (
      <div style={{ display: 'flex' }}>
        {showRolesEditIcon && (
          <IconButton
            icon={<EditOutlined />}
            tooltip={t('Upraviť')}
            onClick={(e) => {
              e.stopPropagation();
              onEditUserProfileClick(user.person_id);
            }}
          />
        )}

        <IconButton
          icon={<UserOutlined />}
          tooltip={t('Role')}
          onClick={(e) => {
            e.stopPropagation();
            onDetailClick(user.person_id);
          }}
        />
      </div>
    );
  };

  if (!users) {
    return <Spinner />;
  }

  return (
    <>
      <DataGrid
        rowKey={(department) => department.person_id}
        columns={columns}
        dataSource={users || []}
        customSettings={customSettings}
        onRowClick={
          showRolesEditIcon &&
          ((role) => onEditUserProfileClick(role.person_id))
        }
        permissionsBasic={'users'}
        uniqueTableId={'users-table'}
      />
    </>
  );
};

UsersTable.propTypes = {
  users: PropTypes.array,
  onDelete: PropTypes.func,
  onDetailClick: PropTypes.func,
  onCreateClick: PropTypes.func,
  onEditUserProfileClick: PropTypes.func,
};

export default UsersTable;
